import { Filetree } from "../datastructure";
import { RenderableData } from "../fetchFiles";
import { FileStatusUpdate } from "../upload/useFileStatusUpdates";

/**
 * Handle a WebSocket file status update.
 * Updates existing files or adds new files to the tree.
 * Handles both single-drive and across-drives modes.
 */
export function handleFileStatusUpdate(
  filetree: Filetree,
  update: FileStatusUpdate,
  isAcrossDrivesMode: boolean
): void {
  // Skip .empty placeholder files used for folder creation
  if (update.fileType === "empty") {
    return;
  }

  // Build partial payload with only defined fields (for merging with existing)
  const partialPayload: Partial<RenderableData> = {
    type: "file",
    status: update.status,
    taskId: update.taskId,
  };
  if (update.blobId !== undefined) partialPayload.fileId = update.blobId;
  if (update.fileName !== undefined) partialPayload.fileName = update.fileName;
  if (update.fileType !== undefined) partialPayload.fileType = update.fileType;
  if (update.uploadedAt !== undefined)
    partialPayload.uploadedAt = update.uploadedAt || null;
  if (update.documentDate !== undefined)
    partialPayload.documentDate = update.documentDate || null;
  if (update.isBlob !== undefined) partialPayload.isBlob = update.isBlob;
  if (update.isPasswordProtected !== undefined)
    partialPayload.isPasswordProtected = update.isPasswordProtected;
  if (update.hasStoredPassword !== undefined)
    partialPayload.hasStoredPassword = update.hasStoredPassword;
  if (update.driveId !== undefined)
    partialPayload.driveId = update.driveId || null;

  // Try to update existing file first
  const existingDatum = filetree.datums.find(
    (d) => d.payload?.type === "file" && d.payload.taskId === update.taskId
  );

  // Build full path if we have the required info (for potential file move)
  let newPath: string[] | undefined;
  // A blob with no filesystem placement sends filesystemPath as NULL, not
  // undefined, so an `!== undefined` guard lets it through and the spread
  // below throws — killing this listener and, with it, the "file is ready"
  // signal the chat composer waits on. The user is then left with a spinning
  // attachment and sends their message without the file (#11242).
  if (Array.isArray(update.filesystemPath) && update.fileName) {
    newPath = [...update.filesystemPath, update.fileName];

    if (isAcrossDrivesMode && update.driveId) {
      const driveDatum = filetree.datums.find(
        (d) => d.path.length === 1 && d.payload?.driveId === update.driveId
      );
      if (driveDatum?.name) {
        newPath = [driveDatum.name, ...newPath];
      }
    }
  }

  if (filetree.updateFileByTaskId(update.taskId, partialPayload, newPath)) {
    return;
  }

  // File not in tree - add it if we have the required info. An empty array is
  // legitimate (a root-level file); null means the blob was never placed in the
  // filesystem at all, and there is no path to add it at.
  if (!Array.isArray(update.filesystemPath) || !update.fileName) {
    return;
  }

  // For new files, build a full payload
  const fullPayload: RenderableData = {
    type: "file",
    fileId: update.blobId,
    fileName: update.fileName || "",
    fileType: update.fileType || "",
    uploadedAt: update.uploadedAt || null,
    documentDate: update.documentDate || null,
    status: update.status,
    isBlob: update.isBlob ?? true,
    isPasswordProtected: update.isPasswordProtected ?? false,
    hasStoredPassword: update.hasStoredPassword ?? false,
    taskId: update.taskId,
    driveId: update.driveId || null,
  };

  const basePath = [...update.filesystemPath, update.fileName || ""];

  if (isAcrossDrivesMode) {
    // In across-drives mode, we need the drive name prefix
    if (!update.driveId) {
      return;
    }
    const driveDatum = filetree.datums.find(
      (d) => d.path.length === 1 && d.payload?.driveId === update.driveId
    );
    if (!driveDatum?.name) {
      return;
    }
    filetree.addFile([driveDatum.name, ...basePath], fullPayload);
  } else {
    // Single-drive mode: use path directly
    filetree.addFile(basePath, fullPayload);
  }
}
